import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Check, X } from "lucide-react";
import type { SandboxTheme } from "../sandbox/SandboxResumen";
import { useBackClose } from "./navHistory";

const MOBILE_QUERY = "(max-width: 767px)";

/** Pantalla compacta (teléfono): el módulo cambia de diseño, no solo de tamaño. */
export function useIsMobile(): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return matches;
}

/** Los elementos fijos viven en <body> y heredan los colores del módulo por `data-theme`. */
function ChromePortal({ theme, children }: { theme: SandboxTheme; children: ReactNode }) {
  return createPortal(
    <div className="sbx sbx-m-chrome" data-theme={theme}>
      {children}
    </div>,
    document.body,
  );
}

export interface BottomNavItem {
  id: string;
  label: string;
  icon: ReactNode;
  badge?: number;
}

/** Navegación inferior de Material 3: de 3 a 5 destinos, con etiqueta siempre visible. */
export function MobileBottomNav({
  items,
  active,
  onSelect,
  theme,
}: {
  items: BottomNavItem[];
  active: string;
  onSelect: (id: string) => void;
  theme: SandboxTheme;
}) {
  return (
    <ChromePortal theme={theme}>
      <nav className="sbx-m-bottomnav" aria-label="Secciones" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
        {items.map((it) => (
          <button
            key={it.id}
            type="button"
            className="sbx-m-navitem"
            aria-current={active === it.id ? "page" : undefined}
            onClick={() => onSelect(it.id)}
          >
            <span className="sbx-m-navicon">
              {it.icon}
              {it.badge ? <span className="sbx-m-navbadge">{it.badge > 99 ? "99+" : it.badge}</span> : null}
            </span>
            <span>{it.label}</span>
          </button>
        ))}
      </nav>
    </ChromePortal>
  );
}

/** Botón flotante: extendido al subir o arriba de la página, solo ícono al bajar (patrón de Material 3). */
export function MobileFab({
  label,
  icon,
  badge,
  onClick,
  theme,
}: {
  label: string;
  icon: ReactNode;
  badge?: string;
  onClick: () => void;
  theme: SandboxTheme;
}) {
  const [compacto, setCompacto] = useState(false);
  useEffect(() => {
    let ultimo = window.scrollY;
    let pendiente = false;
    const alDesplazar = () => {
      if (pendiente) return;
      pendiente = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        if (y < 80) setCompacto(false);
        else if (y > ultimo + 6) setCompacto(true);
        else if (y < ultimo - 6) setCompacto(false);
        ultimo = y;
        pendiente = false;
      });
    };
    window.addEventListener("scroll", alDesplazar, { passive: true });
    return () => window.removeEventListener("scroll", alDesplazar);
  }, []);

  return (
    <ChromePortal theme={theme}>
      <button
        type="button"
        className={`sbx-m-fab${compacto ? " is-compact" : ""}`}
        onClick={onClick}
        aria-haspopup="dialog"
        aria-label={badge ? `${label}: ${badge}` : label}
      >
        {icon}
        {!compacto && <span>{label}</span>}
        {!compacto && badge ? <span className="sbx-m-fab-badge">{badge}</span> : null}
      </button>
    </ChromePortal>
  );
}

/** Hoja inferior modal: filtros y acciones que complementan la pantalla. */
export function MobileSheet({
  open,
  title,
  onClose,
  theme,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  theme: SandboxTheme;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  useBackClose(open, onClose);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panel.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <ChromePortal theme={theme}>
      <div className="sbx-m-backdrop" onClick={onClose} />
      <div className="sbx-m-sheet" role="dialog" aria-modal="true" aria-label={title} ref={panel} tabIndex={-1}>
        <div className="sbx-m-handle" aria-hidden="true" />
        <div className="sbx-m-sheet-head">
          <h2>{title}</h2>
          <button type="button" className="sbx-m-iconbtn" aria-label="Cerrar" onClick={onClose}>
            <X size={22} />
          </button>
        </div>
        <div className="sbx-m-sheet-body">{children}</div>
        {footer ? <div className="sbx-m-sheet-foot">{footer}</div> : null}
      </div>
    </ChromePortal>
  );
}

/** Opción de selección única, de 52 px de alto. */
export function SheetOption({ label, hint, selected, onClick }: { label: string; hint?: string; selected: boolean; onClick: () => void }) {
  return (
    <button type="button" role="radio" aria-checked={selected} className={`sbx-m-option${selected ? " is-on" : ""}`} onClick={onClick}>
      <span>
        {label}
        {hint ? <small>{hint}</small> : null}
      </span>
      {selected ? <Check size={20} aria-hidden="true" /> : null}
    </button>
  );
}

/** Pantalla completa con barra superior y flecha atrás: el detalle de una lista. */
export function MobileScreen({
  title,
  onBack,
  theme,
  children,
}: {
  title: string;
  onBack: () => void;
  theme: SandboxTheme;
  children: ReactNode;
}) {
  useBackClose(true, onBack);
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);
  return (
    <ChromePortal theme={theme}>
      <div className="sbx-m-screen" role="dialog" aria-modal="true" aria-label={title}>
        <header className="sbx-m-screen-bar">
          <button type="button" className="sbx-m-iconbtn" aria-label="Volver" onClick={onBack}>
            <span aria-hidden="true" style={{ fontSize: 22, lineHeight: 1 }}>←</span>
          </button>
          <h2>{title}</h2>
        </header>
        <div className="sbx-m-screen-body">{children}</div>
      </div>
    </ChromePortal>
  );
}
